import { Button, DatePicker, Modal } from 'antd';
import locale from 'antd/es/date-picker/locale/ko_KR';
import moment from 'moment';
import 'moment/locale/ko';
import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { httpGet, httpUrl } from '../../api/httpClient';
import { orderStatusCode } from '../../lib/util/codeUtil';
import { comma } from '../../lib/util/numberUtil';
import DownloadIcon from '../../img/body/download.png';

const { RangePicker } = DatePicker;
const dateFormat = 'YYYY-MM-DD';
const now = new Date();
const PagedListSize = 5;

const header = [
  {
    createDate: '일시',
    dailyCount: '총 건수',
    dailyCountApprove: '승인건수',
    dailyCountCancel: '취소건수',
    priceSum: '매출 합계',
  },
];

const defDateType = {
  daily: 0,
  weekly: 1,
  monthly: 2,
  range: 3,
};

const defOrderStatusType = {
  all: 0,
  complete: 1,
  cancel: 2,
};

const CardStatics = () => {
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    startDate: now,
    endDate: now,
  });

  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedRow, setSelectedRow] = useState(null);
  const [dateType, setDateType] = useState(defDateType.daily);
  const [setOrderStatusType] = useState(defOrderStatusType.all);
  const [isOpenRangePicker, setIsOpenRangePicker] = useState(false);

  useEffect(() => {
    const getListAsync = async () => {
      const pageNum = pagination.current;
      const pageSize = pagination.pageSize;
      const startDate = moment(pagination.startDate).format(dateFormat);
      const endDate = moment(pagination.endDate)
        .add(1, 'day')
        .format(dateFormat);

      setList([]);
      setTotal(0);

      const res = await httpGet(
        httpUrl.cardStaticsList,
        [endDate, pageNum, pageSize, startDate],
        {}
      );
      if (res.result !== 'SUCCESS') {
        // console.log(`getListAsync failed. result: ${res.result}`);
        return;
      }

      const { cardPaymentSums } = res.data;

      const autorizedPayments = cardPaymentSums.filter(
        (item) => item.authType === 0
      );
      const canceledPayments = cardPaymentSums.filter(
        (item) => item.authType === 1
      );
      let result = [];

      autorizedPayments.forEach((payment) => {
        const idx = canceledPayments.findIndex(
          (cpayment) => payment.createDate === cpayment.createDate
        );
        if (idx !== -1) {
          let newPayment = { ...payment };
          newPayment.priceSum =
            payment.priceSum - canceledPayments[idx].priceSum;
          newPayment.taxSum = payment.taxSum - canceledPayments[idx].taxSum;
          newPayment['dailyCountCancel'] = canceledPayments[idx].dailyCount;
          result.push(newPayment);
        } else {
          let newPayment = { ...payment };
          newPayment['dailyCountCancel'] = 0;
          result.push(newPayment);
        }
      });

      setList(result);
      setTotal(res.data.totalCount);
    };

    getListAsync();
  }, [pagination]);

  const onChange = (page) => {
    setPagination({
      ...pagination,
      current: page,
    });
  };

  const onChangeDatePickerStart = (_, dateString) => {
    if (dateString) {
      if (new Date(pagination.endDate) - new Date(dateString) > 0) {
        setPagination({
          ...pagination,
          current: 1,
          startDate: dateString,
        });
      } else {
        setPagination({
          ...pagination,
          current: 1,
          startDate: pagination.endDate,
          endDate: dateString,
        });
      }
      setDateType(defDateType.range);
    } else {
      setPagination({
        ...pagination,
        current: 1,
        startDate: moment(now).add(-3, 'M').format(dateFormat),
        endDate: now,
      });
      setDateType(defDateType.range);
    }
  };

  const onChangeDatePickerEnd = (_, dateString) => {
    if (dateString) {
      if (new Date(pagination.startDate) - new Date(dateString) < 0) {
        setPagination({
          ...pagination,
          current: 1,
          endDate: dateString,
        });
      } else {
        setPagination({
          ...pagination,
          current: 1,
          startDate: dateString,
          endDate: pagination.startDate,
        });
      }
      setDateType(defDateType.range);
    } else {
      setPagination({
        ...pagination,
        current: 1,
        startDate: moment(now).add(-3, 'M').format(dateFormat),
        endDate: now,
      });
      setDateType(defDateType.range);
    }
  };

  const onChangeDateType = (dateType) => {
    let startDate, endDate;
    let isOpenRangePicker = false;

    switch (dateType) {
      case defDateType.daily:
        startDate = now;
        endDate = now;
        break;

      case defDateType.weekly:
        startDate = moment(now).add(-7, 'days');
        endDate = now;
        break;

      case defDateType.monthly:
        startDate = moment(now).add(-30, 'days');
        endDate = now;
        break;

      case defDateType.range:
        isOpenRangePicker = true;
        break;

      default:
        return;
    }

    setDateType(dateType);
    setIsOpenRangePicker(isOpenRangePicker);

    if (dateType !== defDateType.range) {
      setPagination({
        ...pagination,
        current: 1,
        startDate: startDate,
        endDate: endDate,
      });
    }
  };

  const onDownload = async () => {
    const startDate = moment(pagination.startDate).format(dateFormat);
    const endDate = moment(pagination.endDate).add(1, 'day').format(dateFormat);
    const pageNum = 1;
    const pageSize = 100000000;

    const res = await httpGet(
      httpUrl.cardStaticsList,
      [endDate, pageNum, pageSize, startDate],
      {}
    );
    if (res.result !== 'SUCCESS') {
      Modal.error({
        title: '엑셀 다운로드 오류',
        content: (
          <div>
            엑셀로 저장하는 동안 오류가 발생했습니다. 다시 시도해 주십시오.
          </div>
        ),
      });
      return;
    }

    // console.log(res);

    const { cardPaymentSums } = res.data;

    const autorizedPayments = cardPaymentSums.filter(
      (item) => item.authType === 0
    );
    const canceledPayments = cardPaymentSums.filter(
      (item) => item.authType === 1
    );
    let resultExcel = [];

    autorizedPayments.forEach((payment) => {
      const idx = canceledPayments.findIndex(
        (cpayment) => payment.createDate === cpayment.createDate
      );
      if (idx !== -1) {
        let newPayment = { ...payment };
        newPayment.priceSum = payment.priceSum - canceledPayments[idx].priceSum;
        newPayment.taxSum = payment.taxSum - canceledPayments[idx].taxSum;
        newPayment['dailyCountCancel'] = canceledPayments[idx].dailyCount;
        resultExcel.push(newPayment);
      } else {
        let newPayment = { ...payment };
        newPayment['dailyCountCancel'] = 0;
        resultExcel.push(newPayment);
      }
    });

    const title = '통계';
    const filename = moment().format('YYYYMMDDhhmmss') + '_' + title + '.xlsx';

    const jsonData = resultExcel.map((row) => ({
      createDate: moment(row.createDate).format('YYYY-MM-DD'),
      dailyCount: row.dailyCount,
      dailyCountApprove: row.dailyCount - row.dailyCountCancel,
      dailyCountCancel: row.dailyCountCancel,
      priceSum: row.priceSum + row.taxSum,
    }));

    const array = header.concat(jsonData);

    var ws = XLSX.utils.json_to_sheet(array, { skipHeader: true });
    var wb = XLSX.utils.book_new();

    ws['!cols'] = [];
    ws['!cols'][0] = { width: 15 };

    XLSX.utils.book_append_sheet(wb, ws, '카드통계');
    XLSX.writeFile(wb, filename);
  };

  const onChangeOrderStatusType = (orderStatusType) => {
    let orderStatuses = [];
    switch (orderStatusType) {
      case defOrderStatusType.all:
        orderStatuses.push(orderStatusCode.complete, orderStatusCode.cancel);
        break;

      case defOrderStatusType.complete:
        orderStatuses.push(orderStatusCode.complete);
        break;

      case defOrderStatusType.cancel:
        orderStatuses.push(orderStatusCode.cancel);
        break;

      default:
        return;
    }

    setOrderStatusType(orderStatusType);
    setPagination({
      ...pagination,
      orderStatuses,
    });
  };

  return (
    <div className="card-statics-complete">
      {/* <DeliveryDetailModal
                data={selectedRow}
                isOpen={detailModalOpen}
                close={closeDetailModal}
            /> */}

      <div className="card-statics-menu">
        <div
          className="delivery-element-card-btn-excel mobileoff"
          onClick={() => {}}>
          <Button className="excel-card" onClick={() => onDownload()}>
            엑셀 다운로드 &nbsp;
            <img
              style={{ display: 'inline' }}
              src={DownloadIcon}
              className="download-img"
              alt=""
            />
          </Button>
        </div>
        <div className="card-statics-menu-all">
          <div className="card-statics-menu-text2">
            <div className="card-statics-menu-2">
              <div className="card-statics-menu-text statics">기간조회</div>
              <div
                className={
                  'card-statics-menu-text2 mobileoff' +
                  (dateType === defDateType.daily ? ' active2' : '')
                }
                onClick={() => onChangeDateType(defDateType.daily)}>
                오늘
              </div>
              {/* <div
                className={
                  "card-statics-menu-text2 mobileoff" +
                  (dateType === defDateType.weekly ? " active2" : "")
                }
                onClick={() => onChangeDateType(defDateType.weekly)}
              >
                일주일
              </div> */}
              <div
                className={
                  'card-statics-menu-text2 mobileoff' +
                  (dateType === defDateType.monthly ? ' active2' : '')
                }
                onClick={() => onChangeDateType(defDateType.monthly)}>
                한달
              </div>
              <div
                className={
                  'card-statics-menu-text2 mobileoff' +
                  (dateType === defDateType.range
                    ? ' active2'
                    : ' del-menu-text2-sub')
                }
                onClick={() => onChangeDateType(defDateType.range)}>
                날짜지정
              </div>

              <div className="desk">
                <DatePicker
                  placeholder={'시작일'}
                  locale={locale}
                  style={{
                    marginLeft: '10px',
                    height: '50px',
                    borderRadius: '10px',
                    marginTop: '-5px',
                    textAlign: 'center',
                  }}
                  size="large"
                  format={dateFormat}
                  value={moment(pagination.startDate, dateFormat)}
                  onChange={onChangeDatePickerStart}
                />
                <DatePicker
                  placeholder={'종료일'}
                  locale={locale}
                  style={{
                    // marginLeft: "10px",
                    height: '50px',
                    borderRadius: '10px',
                    marginTop: '-5px',
                    textAlign: 'center',
                  }}
                  size="large"
                  format={dateFormat}
                  value={moment(pagination.endDate, dateFormat)}
                  onChange={onChangeDatePickerEnd}
                />
              </div>

              <div className="pos">
                <DatePicker
                  placeholder={'시작일'}
                  locale={locale}
                  style={{
                    marginLeft: '10px',
                    height: '40px',
                    borderRadius: '10px',
                    marginTop: '-5px',
                    textAlign: 'center',
                  }}
                  size="middle"
                  format={dateFormat}
                  value={moment(pagination.startDate, dateFormat)}
                  onChange={onChangeDatePickerStart}
                />
                <DatePicker
                  placeholder={'종료일'}
                  locale={locale}
                  style={{
                    // marginLeft: "10px",
                    height: '40px',
                    borderRadius: '10px',
                    marginTop: '-5px',
                    textAlign: 'center',
                  }}
                  size="middle"
                  format={dateFormat}
                  value={moment(pagination.endDate, dateFormat)}
                  onChange={onChangeDatePickerEnd}
                />
              </div>

              <div className="mobile">
                <DatePicker
                  placeholder={'시작일'}
                  locale={locale}
                  style={{
                    height: '32px',
                    borderRadius: '6px',
                    marginTop: '5px',
                  }}
                  size="small"
                  format={dateFormat}
                  value={moment(pagination.startDate, dateFormat)}
                  onChange={onChangeDatePickerStart}
                />
                <DatePicker
                  placeholder={'종료일'}
                  locale={locale}
                  style={{
                    height: '32px',
                    borderRadius: '6px',
                    marginTop: '5px',
                  }}
                  size="small"
                  format={dateFormat}
                  value={moment(pagination.endDate, dateFormat)}
                  onChange={onChangeDatePickerEnd}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card-statics-complete-all">
        <div className="card-statics-complete-block">
          <div className="card-statics-complete-block-menu">
            {/* <div className="card-statics-complete-block-menu-text mobileoff">
                            주문번호
                        </div> */}
            <div className="card-statics-complete-block-menu-text">일시</div>
            {/* <div className="card-statics-complete-block-menu-text del-com3">
                            고객정보
                        </div> */}
            <div className="card-statics-complete-block-menu-text">
              일자별 건수
            </div>
            <div className="card-statics-complete-block-menu-text">
              카드매출 합계
            </div>

            {/* <div className="card-statics-complete-block-menu-text mobile">
                            상세
                        </div> */}
          </div>
        </div>
        <div className="cardStatics-wrapper">
          {(!list || list.length === 0) && (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              내역이 없습니다.
            </div>
          )}
          {list &&
            list.map((row, index) => (
              <div
                key={index}
                className={
                  'card-statics-complete-block-field' +
                  (index % 2 === 0 ? '' : ' odd')
                }
                // onClick={() => openDetailModal(row)}
              >
                {/* <div className="card-statics-complete-block-field-text mobileoff">
                                    {row.idx}
                                </div> */}
                <div className="card-statics-complete-block-field-text">
                  {moment(row.createDate).format('YYYY-MM-DD')}
                </div>
                {/* <div className="card-statics-complete-block-field-text del-comsub3">
                                    {row.destAddr1}
                                    {row.destAddr2}
                                    <div className="card-statics-complete-block-field-text-sub">
                                        {row.custPhone}
                                    </div>
                                </div> */}
                <div className="card-statics-complete-block-field-text">
                  총{row.dailyCount}건 (승인
                  {row.dailyCount - row.dailyCountCancel}건/취소
                  {row.dailyCountCancel}건)
                </div>
                <div className="card-statics-complete-block-field-text">
                  {comma(row.priceSum + row.taxSum)}원
                </div>

                {/* <div className="card-statics-complete-block-field-text mobile">
                                    <Button className="del-btn">상세</Button>
                                </div> */}
              </div>
            ))}
        </div>
      </div>

      {/* <div className="card-statics-btn">
        <Pagination
          current={pagination.current}
          total={total}
          pageSize={pagination.pageSize}
          pagedListSize={PagedListSize}
          onChange={onChange}
        />
      </div> */}
    </div>
  );
};

export default CardStatics;
